import { useEffect, useState, type FormEvent } from 'react'
import { Trash2 } from 'lucide-react'
import { api, type ApiError } from '../../lib/api'
import { useAuthStore } from '../../lib/auth-store'
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../ui/card'
import { Button } from '../ui/button'
import { Badge, StatusBox } from '../ui/badge'
import { FloatingInput, FloatingSelect, Select } from '../ui/input'

type Role = 'admin' | 'editor' | 'viewer' | 'commenter'

const TH = 'h-10 px-2 text-left text-xs font-medium text-muted-foreground'

/**
 * Members + pending invites. Admins (and the owner) can:
 *   • invite a new member by email + role
 *   • change a non-owner member's role
 *   • remove a non-owner member
 *   • revoke a pending invite before it's accepted
 *
 * Everyone else (editor/viewer/commenter) sees a read-only list.
 * The action buttons are simply omitted for non-admins so the UI
 * accurately reflects what they can do — server enforces the same.
 */
export function MembersSection({ accessToken }: { accessToken: string | null }) {
  const tenantRole = useAuthStore((s) => s.tenant?.role)
  const currentUserId = useAuthStore((s) => s.user?.id)
  const canManage = tenantRole === 'owner' || tenantRole === 'admin'

  const [members, setMembers] = useState<
    Array<{ id: string; email: string; name: string | null; role: string; joinedAt: string }>
  >([])
  const [invites, setInvites] = useState<
    Array<{ id: string; email: string; role: string; createdAt: string; expiresAt: string }>
  >([])
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState<string | null>(null)
  const [reloadKey, setReloadKey] = useState(0)
  const [busyId, setBusyId] = useState<string | null>(null)

  // Invite form
  const [inviteEmail, setInviteEmail] = useState('')
  const [inviteRole, setInviteRole] = useState<Role>('editor')
  const [inviting, setInviting] = useState(false)

  useEffect(() => {
    if (!accessToken) return
    setLoading(true)
    api
      .getMembers(accessToken)
      .then((res) => {
        setMembers(res.members)
        setInvites(res.pendingInvites)
        setErr(null)
      })
      .catch((e: ApiError) => setErr(e.title ?? 'Failed to load members'))
      .finally(() => setLoading(false))
  }, [accessToken, reloadKey])

  async function handleInvite(e: FormEvent) {
    e.preventDefault()
    if (!accessToken) return
    const email = inviteEmail.trim()
    if (!email) return
    setInviting(true)
    setErr(null)
    try {
      await api.invite(accessToken, email, inviteRole)
      setInviteEmail('')
      setReloadKey((k) => k + 1)
    } catch (e) {
      const ae = e as ApiError
      setErr(ae.detail ?? ae.title ?? 'Invite failed')
    } finally {
      setInviting(false)
    }
  }

  async function handleChangeRole(userId: string, newRole: Role) {
    if (!accessToken) return
    setBusyId(userId)
    setErr(null)
    try {
      await api.changeMemberRole(accessToken, userId, newRole)
      setReloadKey((k) => k + 1)
    } catch (e) {
      const ae = e as ApiError
      setErr(ae.detail ?? ae.title ?? 'Role change failed')
    } finally {
      setBusyId(null)
    }
  }

  async function handleRemoveMember(userId: string, email: string) {
    if (!accessToken) return
    if (!window.confirm(`Remove ${email} from the workspace? They'll lose access immediately.`)) return
    setBusyId(userId)
    setErr(null)
    try {
      await api.removeMember(accessToken, userId)
      setReloadKey((k) => k + 1)
    } catch (e) {
      const ae = e as ApiError
      setErr(ae.detail ?? ae.title ?? 'Remove failed')
    } finally {
      setBusyId(null)
    }
  }

  async function handleRevokeInvite(inviteId: string, email: string) {
    if (!accessToken) return
    if (!window.confirm(`Revoke the pending invite for ${email}?`)) return
    setBusyId(inviteId)
    setErr(null)
    try {
      await api.revokeInvite(accessToken, inviteId)
      setReloadKey((k) => k + 1)
    } catch (e) {
      const ae = e as ApiError
      setErr(ae.detail ?? ae.title ?? 'Revoke failed')
    } finally {
      setBusyId(null)
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Members</CardTitle>
        <CardDescription>
          Workspace members and their roles.{' '}
          {canManage
            ? 'Invite new members below and adjust roles as your team changes.'
            : 'Only admins and the workspace owner can change membership.'}
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-6">
        {err && <StatusBox tone="destructive">{err}</StatusBox>}

        {loading ? (
          <div className="space-y-2">
            <div className="dwlt-skeleton h-10" />
            <div className="dwlt-skeleton h-10" />
          </div>
        ) : (
          <>
            <div className="overflow-x-auto rounded-xl border">
              <table className="w-full text-sm">
                <thead className="bg-muted/30">
                  <tr>
                    <th className={TH}>Member</th>
                    <th className={TH}>Role</th>
                    <th className={TH}>Joined</th>
                    {canManage && <th className={TH} />}
                  </tr>
                </thead>
                <tbody>
                  {members.map((m) => {
                    const isSelf = m.id === currentUserId
                    const isOwner = m.role === 'owner'
                    const canEdit = canManage && !isSelf && !isOwner
                    return (
                      <tr key={m.id} className="border-b last:border-b-0 hover:bg-muted/50">
                        <td className="p-2">
                          <div className="font-medium">{m.name ?? m.email}</div>
                          {m.name && (
                            <div className="text-xs text-muted-foreground">{m.email}</div>
                          )}
                        </td>
                        <td className="p-2">
                          {canEdit ? (
                            <Select
                              disabled={busyId === m.id}
                              value={m.role}
                              onChange={(e) => handleChangeRole(m.id, e.target.value as Role)}
                              className="h-8 w-auto text-xs"
                            >
                              <option value="admin">Admin</option>
                              <option value="editor">Editor</option>
                              <option value="commenter">Commenter</option>
                              <option value="viewer">Viewer</option>
                            </Select>
                          ) : (
                            <Badge variant={isOwner ? 'default' : 'secondary'}>
                              {m.role}
                              {isSelf && ' (you)'}
                            </Badge>
                          )}
                        </td>
                        <td className="p-2 text-xs text-muted-foreground whitespace-nowrap">
                          {new Date(m.joinedAt).toLocaleDateString(undefined, {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                          })}
                        </td>
                        {canManage && (
                          <td className="p-2 text-right">
                            {canEdit && (
                              <Button
                                variant="ghost"
                                size="icon-sm"
                                disabled={busyId === m.id}
                                onClick={() => handleRemoveMember(m.id, m.email)}
                                aria-label={`Remove ${m.email}`}
                                title="Remove member"
                                className="text-muted-foreground hover:text-destructive"
                              >
                                <Trash2 />
                              </Button>
                            )}
                          </td>
                        )}
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>

            {invites.length > 0 && (
              <div className="space-y-2">
                <h3 className="text-sm font-medium">Pending invites</h3>
                <div className="overflow-x-auto rounded-xl border">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/30">
                      <tr>
                        <th className={TH}>Email</th>
                        <th className={TH}>Role</th>
                        <th className={TH}>Expires</th>
                        {canManage && <th className={TH} />}
                      </tr>
                    </thead>
                    <tbody>
                      {invites.map((i) => (
                        <tr key={i.id} className="border-b last:border-b-0 hover:bg-muted/50">
                          <td className="p-2">{i.email}</td>
                          <td className="p-2">
                            <Badge variant="outline">{i.role}</Badge>
                          </td>
                          <td className="p-2 text-xs text-muted-foreground whitespace-nowrap">
                            {new Date(i.expiresAt).toLocaleDateString(undefined, {
                              month: 'short',
                              day: 'numeric',
                            })}
                          </td>
                          {canManage && (
                            <td className="p-2 text-right">
                              <Button
                                variant="ghost"
                                size="sm"
                                disabled={busyId === i.id}
                                onClick={() => handleRevokeInvite(i.id, i.email)}
                              >
                                Revoke
                              </Button>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>

      {canManage && !loading && (
        <CardFooter>
          <form onSubmit={handleInvite} className="flex w-full flex-wrap items-center gap-2">
            <div className="min-w-[220px] flex-1">
              <FloatingInput
                label="Invite by email"
                type="email"
                required
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                className="bg-card"
              />
            </div>
            <div className="w-40">
              <FloatingSelect
                label="Role"
                value={inviteRole}
                onChange={(e) => setInviteRole(e.target.value as Role)}
                className="bg-card"
              >
                <option value="admin">Admin</option>
                <option value="editor">Editor</option>
                <option value="commenter">Commenter</option>
                <option value="viewer">Viewer</option>
              </FloatingSelect>
            </div>
            <Button type="submit" size="sm" disabled={inviting || !inviteEmail.trim()}>
              {inviting ? 'Sending…' : 'Send invite'}
            </Button>
          </form>
        </CardFooter>
      )}
    </Card>
  )
}
